// MyGrades.jsx (completely modernized with new features)
import React, { useState, useEffect } from 'react';
import { useStudentAuth } from '../../context/StudentAuthContext';
import toast from 'react-hot-toast';
import ChartComponent from '../../components/common/ChartComponent';
import Modal from '../../components/common/Modal';

const MyGrades = () => {
  const { studentApi } = useStudentAuth();
  const [grades, setGrades] = useState([]);
  const [subjectPerformances, setSubjectPerformances] = useState([]);
  const [detailedPerformance, setDetailedPerformance] = useState(null);
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [academicSessions, setAcademicSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [loading, setLoading] = useState({
    initial: true,
    detailed: false,
    history: false
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedSubjectDetails, setSelectedSubjectDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [modalTab, setModalTab] = useState('overview');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedSession && selectedTerm) {
      fetchGrades();
      fetchSubjectPerformances();
      fetchDetailedPerformance();
    }
  }, [selectedSession, selectedTerm]);

  const fetchInitialData = async () => {
    try {
      setLoading(prev => ({ ...prev, initial: true }));
      
      const [sessionsRes, performancesRes] = await Promise.all([
        studentApi.get('/student-portal/academic-sessions'),
        studentApi.get('/student-portal/all-subject-performances')
      ]);

      setAcademicSessions(sessionsRes.data || []);
      setSubjectPerformances(performancesRes.data?.subjects || []);

      // Set default session if available
      if (sessionsRes.data && sessionsRes.data.length > 0) {
        const latestSession = sessionsRes.data[0];
        setSelectedSession(latestSession.id);
        
        // Set default term if available
        if (latestSession.terms && latestSession.terms.length > 0) {
          setSelectedTerm(latestSession.terms[0].id);
        }
      }

    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(prev => ({ ...prev, initial: false }));
    }
  };

  const fetchGrades = async () => {
    try {
      const params = {};
      if (selectedSession) params.academic_session_id = selectedSession;
      if (selectedTerm) params.term_id = selectedTerm;
      
      const response = await studentApi.get('/student-portal/grades', { params });
      setGrades(response.data || []);
    } catch (error) {
      toast.error('Failed to load grades');
    }
  };

  const fetchSubjectPerformances = async () => {
    try {
      const params = {};
      if (selectedSession) params.academic_session_id = selectedSession;
      if (selectedTerm) params.term_id = selectedTerm;
      
      const response = await studentApi.get('/student-portal/subject-performances', { params });
      setSubjectPerformances(response.data || []);
    } catch (error) {
      // Fallback to all performances if filtered endpoint fails
      const response = await studentApi.get('/student-portal/all-subject-performances');
      setSubjectPerformances(response.data?.subjects || []);
    }
  };

  const fetchDetailedPerformance = async () => {
    try {
      setLoading(prev => ({ ...prev, detailed: true }));
      const params = {};
      if (selectedSession) params.academic_session_id = selectedSession;
      if (selectedTerm) params.term_id = selectedTerm;
      
      const response = await studentApi.get('/student-portal/student-performance-detailed', { params });
      setDetailedPerformance(response.data);
    } catch (error) {
      console.error('Failed to load detailed performance:', error);
      // Don't show error toast as this is a secondary feature
    } finally {
      setLoading(prev => ({ ...prev, detailed: false }));
    }
  };

  const fetchSubjectHistory = async (subjectId) => {
    try {
      setLoading(prev => ({ ...prev, history: true }));
      const response = await studentApi.get('/student-portal/student-performance-history', {
        params: { subject_id: subjectId }
      });
      setPerformanceHistory(response.data);
    } catch (error) {
      toast.error('Failed to load performance history');
    } finally {
      setLoading(prev => ({ ...prev, history: false }));
    }
  };

  const fetchSubjectAssessmentBreakdown = async (subjectId) => {
    try {
      const params = {};
      if (selectedSession) params.academic_session_id = selectedSession;
      if (selectedTerm) params.term_id = selectedTerm;
      
      const response = await studentApi.get(`/student-portal/subject-assessment-breakdown/${subjectId}`, { params });
      setSelectedSubjectDetails(response.data);
    } catch (error) {
      toast.error('Failed to load assessment breakdown');
    }
  };

  const handleViewSubjectDetails = async (subject) => {
    setSelectedSubject(subject);
    setSelectedSubjectDetails(null);
    setModalTab('overview');
    setShowDetailsModal(true);
    
    // Fetch detailed data for this subject
    await Promise.all([
      fetchSubjectHistory(subject.subject_id),
      fetchSubjectAssessmentBreakdown(subject.subject_id)
    ]);
  };

  const getCurrentSession = () => {
    return academicSessions.find(session => session.id == selectedSession);
  };

  const getCurrentTerm = () => {
    const session = getCurrentSession();
    if (!session || !session.terms) return null;
    return session.terms.find(term => term.id == selectedTerm);
  };

  const getGradeColor = (score) => {
    if (score >= 90) return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-100';
    if (score >= 70) return 'text-blue-600 bg-blue-50 border-blue-100';
    if (score >= 60) return 'text-amber-600 bg-amber-50 border-amber-100';
    if (score >= 50) return 'text-orange-600 bg-orange-50 border-orange-100';
    return 'text-red-600 bg-red-50 border-red-100';
  };

  const getGradeLetter = (score) => {
    if (score >= 90) return 'A+';
    if (score >= 80) return 'A';
    if (score >= 70) return 'B';
    if (score >= 60) return 'C';
    if (score >= 50) return 'D';
    return 'F';
  };

  const getGradeBadge = (score) => {
    if (score >= 90) return { text: 'A+', color: 'bg-emerald-500 text-white' };
    if (score >= 80) return { text: 'A', color: 'bg-green-500 text-white' };
    if (score >= 70) return { text: 'B', color: 'bg-blue-500 text-white' };
    if (score >= 60) return { text: 'C', color: 'bg-amber-500 text-white' };
    if (score >= 50) return { text: 'D', color: 'bg-orange-500 text-white' };
    return { text: 'F', color: 'bg-red-500 text-white' };
  };

  const getPerformanceTrendData = () => {
    if (!grades.length) return null;
    
    const recentGrades = [...grades]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 6)
      .reverse();
    
    return {
      labels: recentGrades.map(grade => 
        `${grade.term || 'Term'} ${grade.academic_session ? `(${grade.academic_session})` : ''}`
      ),
      datasets: [
        {
          label: 'Average Score',
          data: recentGrades.map(grade => grade.average || 0),
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          tension: 0.4,
          fill: true,
          pointBackgroundColor: '#6366f1',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
        }
      ]
    };
  };

  const getSubjectPerformanceChartData = () => {
    if (!subjectPerformances.length) return null;
    
    const topSubjects = [...subjectPerformances]
      .sort((a, b) => (b.latest_score || 0) - (a.latest_score || 0))
      .slice(0, 8);
    
    return {
      labels: topSubjects.map(subject => subject.subject_name),
      datasets: [
        {
          label: 'Latest Score',
          data: topSubjects.map(subject => subject.latest_score || 0),
          backgroundColor: [
            'rgba(99, 102, 241, 0.8)',
            'rgba(14, 165, 233, 0.8)',
            'rgba(34, 197, 94, 0.8)',
            'rgba(234, 179, 8, 0.8)',
            'rgba(239, 68, 68, 0.8)',
            'rgba(168, 85, 247, 0.8)',
            'rgba(236, 72, 153, 0.8)',
            'rgba(6, 182, 212, 0.8)',
          ],
          borderColor: [
            '#6366f1',
            '#0ea5e9',
            '#22c55e',
            '#eab308',
            '#ef4444',
            '#a855f7',
            '#ec4899',
            '#06b6d4',
          ],
          borderWidth: 2,
          borderRadius: 8,
          barPercentage: 0.8,
        }
      ]
    };
  };

  const getProgressData = (subject) => {
    if (!subject.performance_history || subject.performance_history.length < 2) {
      return { direction: 'flat', percentage: 0 };
    }
    
    const latest = subject.performance_history[0]?.score || 0;
    const previous = subject.performance_history[1]?.score || 0;
    const percentage = latest - previous;
    
    return {
      direction: percentage > 0 ? 'up' : percentage < 0 ? 'down' : 'flat',
      percentage: Math.abs(percentage)
    };
  };

  const getAssessmentChartData = () => {
    if (!selectedSubjectDetails?.assessment_breakdown) return null;
    
    const breakdown = selectedSubjectDetails.assessment_breakdown;
    
    return {
      labels: breakdown.map(item => item.field_name),
      datasets: [
        {
          label: 'Score',
          data: breakdown.map(item => item.score),
          backgroundColor: 'rgba(99, 102, 241, 0.8)',
          borderColor: '#6366f1',
          borderWidth: 2,
          borderRadius: 8,
          barPercentage: 0.6,
        },
        {
          label: 'Max Score',
          data: breakdown.map(item => item.max_score),
          backgroundColor: 'rgba(209, 213, 219, 0.8)',
          borderColor: '#d1d5db',
          borderWidth: 2,
          borderRadius: 8,
          barPercentage: 0.6,
        }
      ]
    };
  };

  if (loading.initial && !subjectPerformances.length) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <div className="relative">
          <div className="animate-spin rounded-full h-20 w-20 border-b-2 border-indigo-600"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <i className="fas fa-graduation-cap text-2xl text-indigo-300"></i>
          </div>
        </div>
        <p className="mt-6 text-gray-600 font-medium">Loading your academic records...</p>
        <p className="text-gray-400 text-sm mt-2">Please wait while we fetch your data</p>
      </div>
    );
  }

  const currentSession = getCurrentSession();
  const currentTerm = getCurrentTerm();

  return (
    <div className="space-y-8">
      {/* Header with Gradient */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 p-8 text-white shadow-xl">
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Academic Performance</h1>
              <p className="opacity-90 text-lg">
                Comprehensive view of your grades and progress with detailed assessment breakdown
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                <i className="fas fa-calendar-alt mr-2"></i>
                <span className="font-medium">
                  {currentSession?.academic_session || 'All Sessions'}
                  {currentTerm ? ` • ${currentTerm.term}` : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>
      </div>

      {/* Selection Panel - Modern Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            Academic Session
          </label>
          <div className="relative">
            <select
              value={selectedSession}
              onChange={(e) => {
                setSelectedSession(e.target.value);
                setSelectedTerm('');
              }}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none"
            >
              <option value="">All Sessions</option>
              {academicSessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.academic_session}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-3 text-gray-400">
              <i className="fas fa-chevron-down"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            Term
          </label>
          <div className="relative">
            <select
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              disabled={!selectedSession}
              className={`w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none ${
                !selectedSession ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <option value="">All Terms</option>
              {currentSession?.terms?.map((term) => (
                <option key={term.id} value={term.id}>
                  {term.term}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-3 text-gray-400">
              <i className="fas fa-chevron-down"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            Quick Stats
          </label>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {subjectPerformances.length}
              </div>
              <div className="text-sm text-gray-600">Subjects</div>
            </div>
            <div className="p-3 bg-indigo-100 rounded-xl">
              <i className="fas fa-book text-indigo-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            Actions
          </label>
          <button
            onClick={fetchInitialData}
            disabled={loading.initial}
            className="w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2"
          >
            {loading.initial ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                <span>Loading...</span>
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt"></i>
                <span>Refresh Data</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
        <div className="border-b border-gray-100">
          <div className="flex space-x-1 px-6">
            {['overview', 'subjects', 'detailed-report'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 font-medium text-sm transition-colors relative ${
                  activeTab === tab
                    ? 'text-indigo-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1).replace('-', ' ')}
                {activeTab === tab && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"></div>
                )}
              </button>
            ))}
          </div>
        </div>
        
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Overall Performance */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <div className="bg-white p-6 rounded-xl border border-gray-100">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Performance Overview</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                      <div className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
                        <div className="text-2xl font-bold text-gray-900">
                          {grades.length > 0 ? grades[grades.length - 1]?.average?.toFixed(1) : '--'}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">Current Average</div>
                      </div>
                      <div className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
                        <div className="text-2xl font-bold text-gray-900">
                          {grades.length > 0 ? grades[grades.length - 1]?.position : '--'}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">Class Position</div>
                      </div>
                      <div className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
                        <div className="text-2xl font-bold text-gray-900">
                          {subjectPerformances.length > 0
                            ? (subjectPerformances.reduce((acc, sub) => acc + (sub.latest_score || 0), 0) / subjectPerformances.length).toFixed(1)
                            : '--'}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">Subject Average</div>
                      </div>
                      <div className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
                        <div className="text-2xl font-bold text-gray-900">
                          {Math.max(...subjectPerformances.map(s => s.latest_score || 0)) || '--'}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">Best Score</div>
                      </div>
                    </div>
                    
                    {/* Performance Trend Chart */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-4">Performance Trend</h4>
                      <div className="h-72">
                        {getPerformanceTrendData() ? (
                          <ChartComponent
                            type="line"
                            data={getPerformanceTrendData()}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { display: false },
                                tooltip: {
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                  padding: 12,
                                  cornerRadius: 8,
                                  callbacks: {
                                    label: (context) => `Score: ${context.raw}%`
                                  }
                                }
                              },
                              scales: {
                                y: {
                                  min: 0,
                                  max: 100,
                                  grid: { color: 'rgba(0, 0, 0, 0.05)' },
                                  ticks: { callback: (value) => `${value}%` }
                                },
                                x: { grid: { display: false } }
                              }
                            }}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                              <i className="fas fa-chart-line text-5xl text-gray-300 mb-3"></i>
                              <p className="text-gray-500">No trend data available</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Grades */}
                <div className="bg-white p-6 rounded-xl border border-gray-100">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Recent Grades</h3>
                    <span className="text-sm text-gray-500">Last 5 terms</span>
                  </div>
                  <div className="space-y-4">
                    {grades.slice(-5).reverse().map((grade, index) => (
                      <div key={index} className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 hover:shadow-sm transition-all">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              Term {grade.term}
                            </h4>
                            <p className="text-sm text-gray-500">Session {grade.academic_session}</p>
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-bold ${getGradeColor(grade.average).split(' ')[0]}`}>
                              {grade.average?.toFixed(1)}
                            </div>
                            <div className="text-sm text-gray-500">Pos: {grade.position}</div>
                          </div>
                        </div>
                        {grade.comment && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <i className="fas fa-comment text-gray-400 mt-0.5"></i>
                              <p className="text-sm text-gray-600">{grade.comment}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Subject Performance Chart */}
              <div className="bg-white p-6 rounded-xl border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Subject Performance Distribution</h3>
                <div className="h-80">
                  {subjectPerformances.length > 0 ? (
                    <ChartComponent
                      type="bar"
                      data={getSubjectPerformanceChartData()}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            padding: 12,
                            cornerRadius: 8,
                            callbacks: {
                              label: (context) => `Score: ${context.raw}%`
                            }
                          }
                        },
                        scales: {
                          y: {
                            min: 0,
                            max: 100,
                            grid: { color: 'rgba(0, 0, 0, 0.05)' },
                            ticks: { callback: (value) => `${value}%` }
                          },
                          x: { grid: { display: false } }
                        }
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <i className="fas fa-chart-bar text-5xl text-gray-300 mb-3"></i>
                        <p className="text-gray-500">No subject data available</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Subjects Tab */}
          {activeTab === 'subjects' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Subject Statistics */}
                <div className="lg:col-span-1">
                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-2xl border border-indigo-100">
                    <h4 className="font-bold text-gray-900 mb-4">Subject Summary</h4>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Total Subjects</span>
                        <span className="font-bold text-gray-900">{subjectPerformances.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Excelling (A/A+)</span>
                        <span className="font-bold text-emerald-600">
                          {subjectPerformances.filter(s => s.latest_score >= 80).length}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Good (B)</span>
                        <span className="font-bold text-blue-600">
                          {subjectPerformances.filter(s => s.latest_score >= 70 && s.latest_score < 80).length}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Need Improvement</span>
                        <span className="font-bold text-amber-600">
                          {subjectPerformances.filter(s => s.latest_score < 70).length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Subjects */}
                <div className="lg:col-span-2">
                  <div className="bg-white p-6 rounded-2xl border border-gray-100">
                    <h4 className="font-bold text-gray-900 mb-4">Top Performing Subjects</h4>
                    <div className="space-y-4">
                      {subjectPerformances
                        .sort((a, b) => (b.latest_score || 0) - (a.latest_score || 0))
                        .slice(0, 3)
                        .map((subject, index) => {
                          const progress = getProgressData(subject);
                          return (
                            <div key={index} className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                  <div className="p-3 rounded-lg bg-gradient-to-br from-indigo-100 to-indigo-50">
                                    <i className="fas fa-book text-indigo-600 text-xl"></i>
                                  </div>
                                  <div>
                                    <h5 className="font-bold text-gray-900">{subject.subject_name}</h5>
                                    <p className="text-sm text-gray-500">{subject.subject_code}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className={`text-2xl font-bold ${getGradeColor(subject.latest_score).split(' ')[0]}`}>
                                    {subject.latest_score?.toFixed(1)}%
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    <span className={`inline-flex items-center ${progress.direction === 'up' ? 'text-emerald-600' : progress.direction === 'down' ? 'text-red-600' : 'text-gray-500'}`}>
                                      <i className={`fas fa-arrow-${progress.direction} mr-1 text-xs`}></i>
                                      {progress.percentage.toFixed(1)}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>

              {/* All Subjects Table */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900">All Subjects Performance</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Subject
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Score
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Grade
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Class Avg
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Position
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {subjectPerformances.map((subject, index) => {
                        const gradeBadge = getGradeBadge(subject.latest_score);
                        const progress = getProgressData(subject);
                        return (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-100 to-indigo-50 flex items-center justify-center">
                                  <i className="fas fa-book text-indigo-600"></i>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-semibold text-gray-900">
                                    {subject.subject_name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {subject.subject_code}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className={`text-lg font-bold ${getGradeColor(subject.latest_score).split(' ')[0]}`}>
                                  {subject.latest_score?.toFixed(1)}%
                                </div>
                                {progress.percentage > 0 && (
                                  <span className={`ml-2 text-xs px-2 py-1 rounded-full ${progress.direction === 'up' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                                    <i className={`fas fa-arrow-${progress.direction} mr-1`}></i>
                                    {progress.percentage.toFixed(1)}%
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${gradeBadge.color}`}>
                                {subject.latest_score ? gradeBadge.text : 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {subject.class_average?.toFixed(1) || '--'}%
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {subject.position ? `${subject.position}/${subject.total_students}` : '--'}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => handleViewSubjectDetails(subject)}
                                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors"
                              >
                                <i className="fas fa-chart-line mr-2"></i>
                                View Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Detailed Report Tab */}
          {activeTab === 'detailed-report' && (
            <div className="space-y-6">
              {loading.detailed ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                  <p className="mt-4 text-gray-600">Loading detailed performance report...</p>
                </div>
              ) : detailedPerformance ? (
                <>
                  <div className="bg-white p-6 rounded-2xl border border-gray-100">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Detailed Performance Report</h3>
                        <p className="text-gray-600 mt-1">
                          Showing assessment breakdown for {detailedPerformance.performances?.length || 0} subjects
                        </p>
                      </div>
                      <div className="mt-4 md:mt-0 flex items-center space-x-4">
                        <div className="text-sm text-gray-500">
                          <i className="fas fa-user mr-2"></i>
                          {detailedPerformance.student_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          <i className="fas fa-users mr-2"></i>
                          {detailedPerformance.class_name}
                        </div>
                      </div>
                    </div>

                    {detailedPerformance.performances?.map((performance, index) => (
                      <div key={index} className="mb-6 last:mb-0 border border-gray-200 rounded-xl overflow-hidden">
                        <div className="bg-gray-50 p-4 border-b border-gray-200">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                            <div>
                              <h4 className="font-bold text-lg text-gray-900">{performance.subject_name}</h4>
                              <p className="text-gray-600 text-sm">{performance.subject_code}</p>
                            </div>
                            <div className="flex items-center space-x-6 mt-2 md:mt-0">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-gray-900">{performance.total_score}%</div>
                                <div className="text-sm text-gray-500">Total Score</div>
                              </div>
                              <div className="text-center">
                                <div className={`text-2xl font-bold ${getGradeColor(performance.total_score).split(' ')[0]}`}>
                                  {performance.grade}
                                </div>
                                <div className="text-sm text-gray-500">Grade</div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold text-gray-900">
                                  {performance.position ? `${performance.position}/${performance.total_students}` : '--'}
                                </div>
                                <div className="text-sm text-gray-500">Position</div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {performance.assessment_details && performance.assessment_details.length > 0 && (
                          <div className="p-4">
                            <h5 className="font-semibold text-gray-800 mb-3">Assessment Breakdown</h5>
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Assessment
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Score
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Max Score
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Weight
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Weighted Score
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {performance.assessment_details.map((detail, detailIndex) => (
                                    <tr key={detailIndex} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm text-gray-900">
                                        {detail.assessment_label}
                                        <div className="text-xs text-gray-500">{detail.assessment_code}</div>
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900">
                                        <div className="font-medium">{detail.score.toFixed(1)}</div>
                                        <div className="text-xs text-gray-500">
                                          {(detail.score / detail.max_score * 100).toFixed(1)}%
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900">
                                        {detail.max_score}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900">
                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                          {detail.weight}x
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                        {(detail.score * detail.weight).toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                  <tr className="bg-gray-50 font-semibold">
                                    <td className="px-4 py-3 text-sm text-gray-900" colSpan={4}>
                                      Total Weighted Score
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      {performance.assessment_details.reduce((sum, detail) => 
                                        sum + (detail.score * detail.weight), 0
                                      ).toFixed(2)}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <i className="fas fa-chart-pie text-5xl text-gray-300 mb-4"></i>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No Detailed Report Available</h3>
                  <p className="text-gray-500">
                    Select an academic session and term to view detailed performance report.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Subject Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedSubject(null);
          setSelectedSubjectDetails(null);
        }}
        title={selectedSubject?.subject_name}
        size="xl"
      >
        {selectedSubject && (
          <div className="space-y-6">
            {/* Modal Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex space-x-4">
                {['overview', 'assessment', 'history'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setModalTab(tab)}
                    className={`px-4 py-2 font-medium text-sm transition-colors relative ${
                      modalTab === tab
                        ? 'text-indigo-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    {modalTab === tab && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Overview Tab */}
            {modalTab === 'overview' && (
              <div className="space-y-6">
                {/* Subject Header */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-2xl border border-indigo-100">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div>
                      <div className="text-sm text-gray-600 font-medium">Current Score</div>
                      <div className={`text-3xl font-bold mt-2 ${getGradeColor(selectedSubject.latest_score).split(' ')[0]}`}>
                        {selectedSubject.latest_score?.toFixed(1)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 font-medium">Grade</div>
                      <div className="mt-2">
                        <span className={`px-4 py-2 inline-flex text-sm font-bold rounded-full ${getGradeBadge(selectedSubject.latest_score).color}`}>
                          {getGradeLetter(selectedSubject.latest_score)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 font-medium">Class Average</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">
                        {selectedSubject.class_average?.toFixed(1)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 font-medium">Position</div>
                      <div className="text-2xl font-bold text-gray-900 mt-2">
                        {selectedSubject.position ? `${selectedSubject.position}/${selectedSubject.total_students}` : '--'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance History */}
                {selectedSubject.performance_history && selectedSubject.performance_history.length > 0 && (
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-4">Recent Performance</h4>
                    <div className="space-y-3">
                      {selectedSubject.performance_history.slice(0, 3).map((record, index) => (
                        <div key={index} className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-medium text-gray-900">
                                {record.academic_session} • {record.term}
                              </div>
                              <div className="text-sm text-gray-500 mt-1">
                                Class Average: {record.class_average?.toFixed(1)}%
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`text-xl font-bold ${getGradeColor(record.score).split(' ')[0]}`}>
                                {record.score?.toFixed(1)}%
                              </div>
                              <div className="text-sm text-gray-500">
                                Grade: {getGradeLetter(record.score)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Performance Analysis */}
                <div className="bg-gray-50 p-6 rounded-2xl">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">Performance Analysis</h4>
                  <div className="space-y-4">
                    {selectedSubject.latest_score >= 90 ? (
                      <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
                        <div className="flex items-start space-x-3">
                          <div className="p-2 bg-emerald-100 rounded-lg">
                            <i className="fas fa-star text-emerald-600"></i>
                          </div>
                          <div>
                            <h5 className="font-bold text-emerald-800">Excellent Performance!</h5>
                            <p className="text-emerald-700 mt-1">
                              You're demonstrating exceptional understanding of {selectedSubject.subject_name}. 
                              Consider exploring advanced topics or helping peers.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : selectedSubject.latest_score >= 80 ? (
                      <div className="p-4 bg-green-50 border border-green-100 rounded-xl">
                        <div className="flex items-start space-x-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <i className="fas fa-thumbs-up text-green-600"></i>
                          </div>
                          <div>
                            <h5 className="font-bold text-green-800">Very Good Performance</h5>
                            <p className="text-green-700 mt-1">
                              You have a solid understanding of {selectedSubject.subject_name}. 
                              Focus on mastering complex concepts to reach the next level.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : selectedSubject.latest_score >= 70 ? (
                      <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                        <div className="flex items-start space-x-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <i className="fas fa-chart-line text-blue-600"></i>
                          </div>
                          <div>
                            <h5 className="font-bold text-blue-800">Good Performance</h5>
                            <p className="text-blue-700 mt-1">
                              You're making good progress in {selectedSubject.subject_name}. 
                              Regular practice and review will help improve your scores.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : selectedSubject.latest_score >= 60 ? (
                      <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                        <div className="flex items-start space-x-3">
                          <div className="p-2 bg-amber-100 rounded-lg">
                            <i className="fas fa-lightbulb text-amber-600"></i>
                          </div>
                          <div>
                            <h5 className="font-bold text-amber-800">Satisfactory Performance</h5>
                            <p className="text-amber-700 mt-1">
                              You understand the basics of {selectedSubject.subject_name}. 
                              Consider additional study sessions and asking questions in class.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-red-50 border border-red-100 rounded-xl">
                        <div className="flex items-start space-x-3">
                          <div className="p-2 bg-red-100 rounded-lg">
                            <i className="fas fa-exclamation-circle text-red-600"></i>
                          </div>
                          <div>
                            <h5 className="font-bold text-red-800">Needs Improvement</h5>
                            <p className="text-red-700 mt-1">
                              You may need extra help with {selectedSubject.subject_name}. 
                              Consider meeting with your teacher to discuss learning strategies.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Assessment Breakdown Tab */}
            {modalTab === 'assessment' && (
              <div className="space-y-6">
                {loading.history ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                    <p className="mt-3 text-gray-600">Loading assessment breakdown...</p>
                  </div>
                ) : selectedSubjectDetails ? (
                  <>
                    <div className="bg-white p-6 rounded-xl border border-gray-100">
                      <div className="mb-6">
                        <h4 className="text-lg font-bold text-gray-900 mb-2">Assessment Breakdown</h4>
                        <p className="text-gray-600">
                          Detailed view of individual assessment components for {selectedSubject.subject_name}
                        </p>
                      </div>

                      {getAssessmentChartData() && (
                        <div className="mb-8">
                          <div className="h-64">
                            <ChartComponent
                              type="bar"
                              data={getAssessmentChartData()}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: {
                                    position: 'top',
                                    labels: {
                                      padding: 20,
                                      usePointStyle: true,
                                    }
                                  },
                                  tooltip: {
                                    callbacks: {
                                      label: (context) => {
                                        const datasetLabel = context.dataset.label;
                                        const value = context.raw;
                                        return `${datasetLabel}: ${value}`;
                                      }
                                    }
                                  }
                                },
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    grid: { color: 'rgba(0, 0, 0, 0.05)' }
                                  },
                                  x: { grid: { display: false } }
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Assessment
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Score
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Max Score
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Percentage
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Weight
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Weighted Score
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {selectedSubjectDetails.assessment_breakdown?.map((item, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                  <div className="text-sm font-medium text-gray-900">{item.field_name}</div>
                                  <div className="text-xs text-gray-500">{item.field_type}</div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="text-sm text-gray-900 font-medium">{item.score.toFixed(1)}</div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="text-sm text-gray-900">{item.max_score}</div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center">
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div 
                                        className="bg-indigo-600 h-2 rounded-full" 
                                        style={{ width: `${Math.min(100, (item.score / item.max_score) * 100)}%` }}
                                      ></div>
                                    </div>
                                    <span className="ml-2 text-xs font-medium text-gray-700 min-w-[40px]">
                                      {item.percentage.toFixed(1)}%
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                    {item.weight}x
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="text-sm font-medium text-gray-900">
                                    {item.weighted_score.toFixed(2)}
                                  </div>
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50 font-semibold">
                              <td className="px-4 py-3 text-sm text-gray-900" colSpan={5}>
                                Total Weighted Score
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {selectedSubjectDetails.assessment_breakdown?.reduce((sum, item) => 
                                  sum + item.weighted_score, 0
                                ).toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                        <h5 className="font-semibold text-gray-800 mb-2">Summary</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <div className="text-sm text-gray-600">Total Score</div>
                            <div className="text-xl font-bold text-gray-900">
                              {selectedSubjectDetails.summary?.total_score}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Calculated Score</div>
                            <div className="text-xl font-bold text-gray-900">
                              {selectedSubjectDetails.summary?.calculated_score}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Difference</div>
                            <div className={`text-xl font-bold ${
                              Math.abs(selectedSubjectDetails.summary?.total_score - selectedSubjectDetails.summary?.calculated_score) < 0.01 
                                ? 'text-gray-900' 
                                : 'text-amber-600'
                            }`}>
                              {Math.abs(selectedSubjectDetails.summary?.total_score - selectedSubjectDetails.summary?.calculated_score).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <i className="fas fa-chart-bar text-4xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500">No assessment breakdown available for this subject.</p>
                  </div>
                )}
              </div>
            )}

            {/* History Tab */}
            {modalTab === 'history' && (
              <div className="space-y-6">
                {loading.history ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                    <p className="mt-3 text-gray-600">Loading performance history...</p>
                  </div>
                ) : performanceHistory && performanceHistory.length > 0 ? (
                  <>
                    <div className="mb-6">
                      <h4 className="text-lg font-bold text-gray-900">Performance History</h4>
                      <p className="text-gray-600">
                        Track your performance in {selectedSubject.subject_name} across different academic sessions
                      </p>
                    </div>

                    <div className="space-y-4">
                      {performanceHistory.map((session, index) => (
                        <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="bg-gray-50 p-4 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                              <div>
                                <h5 className="font-bold text-gray-900">{session.academic_session}</h5>
                                <p className="text-sm text-gray-600">Term {session.term} • {session.class_name}</p>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-500">
                                  {session.total_subjects} subjects • Avg: {session.overall_average?.toFixed(1)}%
                                </div>
                              </div>
                            </div>
                          </div>

                          {session.subject_performances?.map((subject, subIndex) => (
                            <div key={subIndex} className="p-4 border-t border-gray-100 hover:bg-gray-50">
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium text-gray-900">{subject.subject_name}</div>
                                  <div className="text-sm text-gray-500 mt-1">
                                    Class Avg: {subject.class_average?.toFixed(1)}% • Position: {subject.position ? `${subject.position}/${subject.total_students}` : '--'}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className={`text-lg font-bold ${getGradeColor(subject.total_score).split(' ')[0]}`}>
                                    {subject.total_score?.toFixed(1)}%
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    Grade: {subject.grade}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <i className="fas fa-history text-4xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500">No performance history available for this subject.</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between pt-4 border-t border-gray-200">
              <div className="flex space-x-2">
                {modalTab !== 'overview' && (
                  <button
                    onClick={() => setModalTab('overview')}
                    className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg font-medium transition-colors"
                  >
                    <i className="fas fa-arrow-left mr-2"></i>
                    Back to Overview
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                Close Details
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyGrades;

// // MyGrades.jsx (completely modernized)
// import React, { useState, useEffect } from 'react';
// import { useStudentAuth } from '../../context/StudentAuthContext';
// import toast from 'react-hot-toast';
// import ChartComponent from '../../components/common/ChartComponent';
// import Modal from '../../components/common/Modal';

// const MyGrades = () => {
//   const { studentApi } = useStudentAuth();
//   const [grades, setGrades] = useState([]);
//   const [subjectPerformances, setSubjectPerformances] = useState([]);
//   const [academicSessions, setAcademicSessions] = useState([]);
//   const [selectedSession, setSelectedSession] = useState('');
//   const [selectedTerm, setSelectedTerm] = useState('');
//   const [loading, setLoading] = useState(true);
//   const [showDetailsModal, setShowDetailsModal] = useState(false);
//   const [selectedSubject, setSelectedSubject] = useState(null);
//   const [activeTab, setActiveTab] = useState('overview');

//   useEffect(() => {
//     fetchInitialData();
//   }, []);

//   useEffect(() => {
//     if (selectedSession) {
//       fetchGrades();
//       fetchSubjectPerformances();
//     }
//   }, [selectedSession, selectedTerm]);

//   const fetchInitialData = async () => {
//     try {
//       setLoading(true);
      
//       const [sessionsRes, performancesRes] = await Promise.all([
//         studentApi.get('/student-portal/academic-sessions'),
//         studentApi.get('/student-portal/all-subject-performances')
//       ]);

//       setAcademicSessions(sessionsRes.data || []);
//       setSubjectPerformances(performancesRes.data?.subjects || []);

//       // Set default session if available
//       if (sessionsRes.data && sessionsRes.data.length > 0) {
//         const latestSession = sessionsRes.data[0];
//         setSelectedSession(latestSession.id);
        
//         // Set default term if available
//         if (latestSession.terms && latestSession.terms.length > 0) {
//           setSelectedTerm(latestSession.terms[0].id);
//         }
//       }

//     } catch (error) {
//       toast.error('Failed to load data');
//       console.error(error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchGrades = async () => {
//     try {
//       const params = {};
//       if (selectedSession) params.academic_session_id = selectedSession;
//       if (selectedTerm) params.term_id = selectedTerm;
      
//       const response = await studentApi.get('/student-portal/grades', { params });
//       setGrades(response.data || []);
//     } catch (error) {
//       toast.error('Failed to load grades');
//     }
//   };

//   const fetchSubjectPerformances = async () => {
//     try {
//       const params = {};
//       if (selectedSession) params.academic_session_id = selectedSession;
//       if (selectedTerm) params.term_id = selectedTerm;
      
//       const response = await studentApi.get('/student-portal/subject-performances', { params });
//       setSubjectPerformances(response.data || []);
//     } catch (error) {
//       // Fallback to all performances if filtered endpoint fails
//       const response = await studentApi.get('/student-portal/all-subject-performances');
//       setSubjectPerformances(response.data?.subjects || []);
//     }
//   };

//   const getCurrentSession = () => {
//     return academicSessions.find(session => session.id == selectedSession);
//   };

//   const getCurrentTerm = () => {
//     const session = getCurrentSession();
//     if (!session || !session.terms) return null;
//     return session.terms.find(term => term.id == selectedTerm);
//   };

//   const getGradeColor = (score) => {
//     if (score >= 90) return 'text-emerald-600 bg-emerald-50 border-emerald-100';
//     if (score >= 80) return 'text-green-600 bg-green-50 border-green-100';
//     if (score >= 70) return 'text-blue-600 bg-blue-50 border-blue-100';
//     if (score >= 60) return 'text-amber-600 bg-amber-50 border-amber-100';
//     if (score >= 50) return 'text-orange-600 bg-orange-50 border-orange-100';
//     return 'text-red-600 bg-red-50 border-red-100';
//   };

//   const getGradeLetter = (score) => {
//     if (score >= 90) return 'A+';
//     if (score >= 80) return 'A';
//     if (score >= 70) return 'B';
//     if (score >= 60) return 'C';
//     if (score >= 50) return 'D';
//     return 'F';
//   };

//   const getGradeBadge = (score) => {
//     if (score >= 90) return { text: 'A+', color: 'bg-emerald-500 text-white' };
//     if (score >= 80) return { text: 'A', color: 'bg-green-500 text-white' };
//     if (score >= 70) return { text: 'B', color: 'bg-blue-500 text-white' };
//     if (score >= 60) return { text: 'C', color: 'bg-amber-500 text-white' };
//     if (score >= 50) return { text: 'D', color: 'bg-orange-500 text-white' };
//     return { text: 'F', color: 'bg-red-500 text-white' };
//   };

//   const getPerformanceTrendData = () => {
//     if (!grades.length) return null;
    
//     const recentGrades = [...grades]
//       .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
//       .slice(0, 6)
//       .reverse();
    
//     return {
//       labels: recentGrades.map(grade => 
//         `${grade.term || 'Term'} ${grade.academic_session ? `(${grade.academic_session})` : ''}`
//       ),
//       datasets: [
//         {
//           label: 'Average Score',
//           data: recentGrades.map(grade => grade.average || 0),
//           borderColor: '#6366f1',
//           backgroundColor: 'rgba(99, 102, 241, 0.1)',
//           tension: 0.4,
//           fill: true,
//           pointBackgroundColor: '#6366f1',
//           pointBorderColor: '#fff',
//           pointBorderWidth: 2,
//           pointRadius: 6,
//         }
//       ]
//     };
//   };

//   const getSubjectPerformanceChartData = () => {
//     if (!subjectPerformances.length) return null;
    
//     const topSubjects = [...subjectPerformances]
//       .sort((a, b) => (b.latest_score || 0) - (a.latest_score || 0))
//       .slice(0, 8);
    
//     return {
//       labels: topSubjects.map(subject => subject.subject_name),
//       datasets: [
//         {
//           label: 'Latest Score',
//           data: topSubjects.map(subject => subject.latest_score || 0),
//           backgroundColor: [
//             'rgba(99, 102, 241, 0.8)',
//             'rgba(14, 165, 233, 0.8)',
//             'rgba(34, 197, 94, 0.8)',
//             'rgba(234, 179, 8, 0.8)',
//             'rgba(239, 68, 68, 0.8)',
//             'rgba(168, 85, 247, 0.8)',
//             'rgba(236, 72, 153, 0.8)',
//             'rgba(6, 182, 212, 0.8)',
//           ],
//           borderColor: [
//             '#6366f1',
//             '#0ea5e9',
//             '#22c55e',
//             '#eab308',
//             '#ef4444',
//             '#a855f7',
//             '#ec4899',
//             '#06b6d4',
//           ],
//           borderWidth: 2,
//           borderRadius: 8,
//           barPercentage: 0.8,
//         }
//       ]
//     };
//   };

//   const getProgressData = (subject) => {
//     if (!subject.performance_history || subject.performance_history.length < 2) {
//       return { direction: 'flat', percentage: 0 };
//     }
    
//     const latest = subject.performance_history[0]?.score || 0;
//     const previous = subject.performance_history[1]?.score || 0;
//     const percentage = latest - previous;
    
//     return {
//       direction: percentage > 0 ? 'up' : percentage < 0 ? 'down' : 'flat',
//       percentage: Math.abs(percentage)
//     };
//   };

//   if (loading && !subjectPerformances.length) {
//     return (
//       <div className="flex flex-col items-center justify-center min-h-[70vh]">
//         <div className="relative">
//           <div className="animate-spin rounded-full h-20 w-20 border-b-2 border-indigo-600"></div>
//           <div className="absolute inset-0 flex items-center justify-center">
//             <i className="fas fa-graduation-cap text-2xl text-indigo-300"></i>
//           </div>
//         </div>
//         <p className="mt-6 text-gray-600 font-medium">Loading your academic records...</p>
//         <p className="text-gray-400 text-sm mt-2">Please wait while we fetch your data</p>
//       </div>
//     );
//   }

//   const currentSession = getCurrentSession();
//   const currentTerm = getCurrentTerm();

//   return (
//     <div className="space-y-8">
//       {/* Header with Gradient */}
//       <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 p-8 text-white shadow-xl">
//         <div className="relative z-10">
//           <div className="flex flex-col md:flex-row md:items-center md:justify-between">
//             <div>
//               <h1 className="text-3xl font-bold mb-2">Academic Performance</h1>
//               <p className="opacity-90 text-lg">
//                 Comprehensive view of your grades and progress
//               </p>
//             </div>
//             <div className="mt-4 md:mt-0">
//               <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
//                 <i className="fas fa-calendar-alt mr-2"></i>
//                 <span className="font-medium">
//                   {currentSession?.academic_session || 'All Sessions'}
//                   {currentTerm ? ` • ${currentTerm.term}` : ''}
//                 </span>
//               </div>
//             </div>
//           </div>
//         </div>
//         {/* Decorative elements */}
//         <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32"></div>
//         <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>
//       </div>

//       {/* Selection Panel - Modern Cards */}
//       <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
//         <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
//           <label className="block text-sm font-semibold text-gray-800 mb-2">
//             Academic Session
//           </label>
//           <div className="relative">
//             <select
//               value={selectedSession}
//               onChange={(e) => {
//                 setSelectedSession(e.target.value);
//                 setSelectedTerm('');
//               }}
//               className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none"
//             >
//               <option value="">All Sessions</option>
//               {academicSessions.map((session) => (
//                 <option key={session.id} value={session.id}>
//                   {session.academic_session}
//                 </option>
//               ))}
//             </select>
//             <div className="absolute right-3 top-3 text-gray-400">
//               <i className="fas fa-chevron-down"></i>
//             </div>
//           </div>
//         </div>

//         <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
//           <label className="block text-sm font-semibold text-gray-800 mb-2">
//             Term
//           </label>
//           <div className="relative">
//             <select
//               value={selectedTerm}
//               onChange={(e) => setSelectedTerm(e.target.value)}
//               disabled={!selectedSession}
//               className={`w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none ${
//                 !selectedSession ? 'opacity-50 cursor-not-allowed' : ''
//               }`}
//             >
//               <option value="">All Terms</option>
//               {currentSession?.terms?.map((term) => (
//                 <option key={term.id} value={term.id}>
//                   {term.term}
//                 </option>
//               ))}
//             </select>
//             <div className="absolute right-3 top-3 text-gray-400">
//               <i className="fas fa-chevron-down"></i>
//             </div>
//           </div>
//         </div>

//         <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
//           <label className="block text-sm font-semibold text-gray-800 mb-2">
//             Quick Stats
//           </label>
//           <div className="flex items-center justify-between">
//             <div>
//               <div className="text-2xl font-bold text-gray-900">
//                 {subjectPerformances.length}
//               </div>
//               <div className="text-sm text-gray-600">Subjects</div>
//             </div>
//             <div className="p-3 bg-indigo-100 rounded-xl">
//               <i className="fas fa-book text-indigo-600 text-xl"></i>
//             </div>
//           </div>
//         </div>

//         <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
//           <label className="block text-sm font-semibold text-gray-800 mb-2">
//             Actions
//           </label>
//           <button
//             onClick={fetchInitialData}
//             disabled={loading}
//             className="w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2"
//           >
//             {loading ? (
//               <>
//                 <i className="fas fa-spinner fa-spin"></i>
//                 <span>Loading...</span>
//               </>
//             ) : (
//               <>
//                 <i className="fas fa-sync-alt"></i>
//                 <span>Refresh Data</span>
//               </>
//             )}
//           </button>
//         </div>
//       </div>

//       {/* Tabs Navigation */}
//       <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
//         <div className="border-b border-gray-100">
//           <div className="flex space-x-1 px-6">
//             {/* {['overview', 'subjects', 'history', 'analytics'].map((tab) => ( */}
//             {['overview', 'subjects'].map((tab) => (
//               <button
//                 key={tab}
//                 onClick={() => setActiveTab(tab)}
//                 className={`px-6 py-4 font-medium text-sm transition-colors relative ${
//                   activeTab === tab
//                     ? 'text-indigo-600'
//                     : 'text-gray-600 hover:text-gray-900'
//                 }`}
//               >
//                 {tab.charAt(0).toUpperCase() + tab.slice(1)}
//                 {activeTab === tab && (
//                   <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"></div>
//                 )}
//               </button>
//             ))}
//           </div>
//         </div>
        
//         <div className="p-6">
//           {/* Overview Tab */}
//           {activeTab === 'overview' && (
//             <div className="space-y-8">
//               {/* Overall Performance */}
//               <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
//                 <div className="lg:col-span-2">
//                   <div className="bg-white p-6 rounded-xl border border-gray-100">
//                     <h3 className="text-xl font-bold text-gray-900 mb-6">Performance Overview</h3>
//                     <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
//                       <div className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
//                         <div className="text-2xl font-bold text-gray-900">
//                           {grades.length > 0 ? grades[grades.length - 1]?.average?.toFixed(1) : '--'}
//                         </div>
//                         <div className="text-sm text-gray-600 mt-1">Current Average</div>
//                       </div>
//                       <div className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
//                         <div className="text-2xl font-bold text-gray-900">
//                           {grades.length > 0 ? grades[grades.length - 1]?.position : '--'}
//                         </div>
//                         <div className="text-sm text-gray-600 mt-1">Class Position</div>
//                       </div>
//                       <div className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
//                         <div className="text-2xl font-bold text-gray-900">
//                           {subjectPerformances.length > 0
//                             ? (subjectPerformances.reduce((acc, sub) => acc + (sub.latest_score || 0), 0) / subjectPerformances.length).toFixed(1)
//                             : '--'}
//                         </div>
//                         <div className="text-sm text-gray-600 mt-1">Subject Average</div>
//                       </div>
//                       <div className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
//                         <div className="text-2xl font-bold text-gray-900">
//                           {Math.max(...subjectPerformances.map(s => s.latest_score || 0)) || '--'}
//                         </div>
//                         <div className="text-sm text-gray-600 mt-1">Best Score</div>
//                       </div>
//                     </div>
                    
//                     {/* Performance Trend Chart */}
//                     <div>
//                       <h4 className="font-semibold text-gray-800 mb-4">Performance Trend</h4>
//                       <div className="h-72">
//                         {getPerformanceTrendData() ? (
//                           <ChartComponent
//                             type="line"
//                             data={getPerformanceTrendData()}
//                             options={{
//                               responsive: true,
//                               maintainAspectRatio: false,
//                               plugins: {
//                                 legend: { display: false },
//                                 tooltip: {
//                                   backgroundColor: 'rgba(0, 0, 0, 0.8)',
//                                   padding: 12,
//                                   cornerRadius: 8,
//                                   callbacks: {
//                                     label: (context) => `Score: ${context.raw}%`
//                                   }
//                                 }
//                               },
//                               scales: {
//                                 y: {
//                                   min: 0,
//                                   max: 100,
//                                   grid: { color: 'rgba(0, 0, 0, 0.05)' },
//                                   ticks: { callback: (value) => `${value}%` }
//                                 },
//                                 x: { grid: { display: false } }
//                               }
//                             }}
//                           />
//                         ) : (
//                           <div className="flex items-center justify-center h-full">
//                             <div className="text-center">
//                               <i className="fas fa-chart-line text-5xl text-gray-300 mb-3"></i>
//                               <p className="text-gray-500">No trend data available</p>
//                             </div>
//                           </div>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Recent Grades */}
//                 <div className="bg-white p-6 rounded-xl border border-gray-100">
//                   <div className="flex justify-between items-center mb-6">
//                     <h3 className="text-xl font-bold text-gray-900">Recent Grades</h3>
//                     <span className="text-sm text-gray-500">Last 5 terms</span>
//                   </div>
//                   <div className="space-y-4">
//                     {grades.slice(-5).reverse().map((grade, index) => (
//                       <div key={index} className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 hover:shadow-sm transition-all">
//                         <div className="flex justify-between items-start">
//                           <div>
//                             <h4 className="font-semibold text-gray-900">
//                               Term {grade.term}
//                             </h4>
//                             <p className="text-sm text-gray-500">Session {grade.academic_session}</p>
//                           </div>
//                           <div className="text-right">
//                             <div className={`text-lg font-bold ${getGradeColor(grade.average).split(' ')[0]}`}>
//                               {grade.average?.toFixed(1)}
//                             </div>
//                             <div className="text-sm text-gray-500">Pos: {grade.position}</div>
//                           </div>
//                         </div>
//                         {grade.comment && (
//                           <div className="mt-3 p-3 bg-gray-50 rounded-lg">
//                             <div className="flex items-start space-x-2">
//                               <i className="fas fa-comment text-gray-400 mt-0.5"></i>
//                               <p className="text-sm text-gray-600">{grade.comment}</p>
//                             </div>
//                           </div>
//                         )}
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//               </div>

//               {/* Subject Performance Chart */}
//               <div className="bg-white p-6 rounded-xl border border-gray-100">
//                 <h3 className="text-xl font-bold text-gray-900 mb-6">Subject Performance Distribution</h3>
//                 <div className="h-80">
//                   {subjectPerformances.length > 0 ? (
//                     <ChartComponent
//                       type="bar"
//                       data={getSubjectPerformanceChartData()}
//                       options={{
//                         responsive: true,
//                         maintainAspectRatio: false,
//                         plugins: {
//                           legend: { display: false },
//                           tooltip: {
//                             backgroundColor: 'rgba(0, 0, 0, 0.8)',
//                             padding: 12,
//                             cornerRadius: 8,
//                             callbacks: {
//                               label: (context) => `Score: ${context.raw}%`
//                             }
//                           }
//                         },
//                         scales: {
//                           y: {
//                             min: 0,
//                             max: 100,
//                             grid: { color: 'rgba(0, 0, 0, 0.05)' },
//                             ticks: { callback: (value) => `${value}%` }
//                           },
//                           x: { grid: { display: false } }
//                         }
//                       }}
//                     />
//                   ) : (
//                     <div className="flex items-center justify-center h-full">
//                       <div className="text-center">
//                         <i className="fas fa-chart-bar text-5xl text-gray-300 mb-3"></i>
//                         <p className="text-gray-500">No subject data available</p>
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Subjects Tab */}
//           {activeTab === 'subjects' && (
//             <div className="space-y-6">
//               <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
//                 {/* Subject Statistics */}
//                 <div className="lg:col-span-1">
//                   <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-2xl border border-indigo-100">
//                     <h4 className="font-bold text-gray-900 mb-4">Subject Summary</h4>
//                     <div className="space-y-4">
//                       <div className="flex justify-between items-center">
//                         <span className="text-gray-600">Total Subjects</span>
//                         <span className="font-bold text-gray-900">{subjectPerformances.length}</span>
//                       </div>
//                       <div className="flex justify-between items-center">
//                         <span className="text-gray-600">Excelling (A/A+)</span>
//                         <span className="font-bold text-emerald-600">
//                           {subjectPerformances.filter(s => s.latest_score >= 80).length}
//                         </span>
//                       </div>
//                       <div className="flex justify-between items-center">
//                         <span className="text-gray-600">Good (B)</span>
//                         <span className="font-bold text-blue-600">
//                           {subjectPerformances.filter(s => s.latest_score >= 70 && s.latest_score < 80).length}
//                         </span>
//                       </div>
//                       <div className="flex justify-between items-center">
//                         <span className="text-gray-600">Need Improvement</span>
//                         <span className="font-bold text-amber-600">
//                           {subjectPerformances.filter(s => s.latest_score < 70).length}
//                         </span>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Top Subjects */}
//                 <div className="lg:col-span-2">
//                   <div className="bg-white p-6 rounded-2xl border border-gray-100">
//                     <h4 className="font-bold text-gray-900 mb-4">Top Performing Subjects</h4>
//                     <div className="space-y-4">
//                       {subjectPerformances
//                         .sort((a, b) => (b.latest_score || 0) - (a.latest_score || 0))
//                         .slice(0, 3)
//                         .map((subject, index) => {
//                           const progress = getProgressData(subject);
//                           return (
//                             <div key={index} className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
//                               <div className="flex items-center justify-between">
//                                 <div className="flex items-center space-x-4">
//                                   <div className="p-3 rounded-lg bg-gradient-to-br from-indigo-100 to-indigo-50">
//                                     <i className="fas fa-book text-indigo-600 text-xl"></i>
//                                   </div>
//                                   <div>
//                                     <h5 className="font-bold text-gray-900">{subject.subject_name}</h5>
//                                     <p className="text-sm text-gray-500">{subject.subject_code}</p>
//                                   </div>
//                                 </div>
//                                 <div className="text-right">
//                                   <div className={`text-2xl font-bold ${getGradeColor(subject.latest_score).split(' ')[0]}`}>
//                                     {subject.latest_score?.toFixed(1)}%
//                                   </div>
//                                   <div className="text-sm text-gray-500">
//                                     <span className={`inline-flex items-center ${progress.direction === 'up' ? 'text-emerald-600' : progress.direction === 'down' ? 'text-red-600' : 'text-gray-500'}`}>
//                                       <i className={`fas fa-arrow-${progress.direction} mr-1 text-xs`}></i>
//                                       {progress.percentage.toFixed(1)}%
//                                     </span>
//                                   </div>
//                                 </div>
//                               </div>
//                             </div>
//                           );
//                         })}
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* All Subjects Table */}
//               <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
//                 <div className="p-6 border-b border-gray-100">
//                   <h3 className="text-xl font-bold text-gray-900">All Subjects Performance</h3>
//                 </div>
//                 <div className="overflow-x-auto">
//                   <table className="min-w-full divide-y divide-gray-200">
//                     <thead className="bg-gray-50">
//                       <tr>
//                         <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
//                           Subject
//                         </th>
//                         <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
//                           Score
//                         </th>
//                         <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
//                           Grade
//                         </th>
//                         <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
//                           Class Avg
//                         </th>
//                         <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
//                           Position
//                         </th>
//                         <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
//                           Actions
//                         </th>
//                       </tr>
//                     </thead>
//                     <tbody className="bg-white divide-y divide-gray-100">
//                       {subjectPerformances.map((subject, index) => {
//                         const gradeBadge = getGradeBadge(subject.latest_score);
//                         const progress = getProgressData(subject);
//                         return (
//                           <tr key={index} className="hover:bg-gray-50 transition-colors">
//                             <td className="px-6 py-4">
//                               <div className="flex items-center">
//                                 <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-100 to-indigo-50 flex items-center justify-center">
//                                   <i className="fas fa-book text-indigo-600"></i>
//                                 </div>
//                                 <div className="ml-4">
//                                   <div className="text-sm font-semibold text-gray-900">
//                                     {subject.subject_name}
//                                   </div>
//                                   <div className="text-xs text-gray-500">
//                                     {subject.subject_code}
//                                   </div>
//                                 </div>
//                               </div>
//                             </td>
//                             <td className="px-6 py-4">
//                               <div className="flex items-center">
//                                 <div className={`text-lg font-bold ${getGradeColor(subject.latest_score).split(' ')[0]}`}>
//                                   {subject.latest_score?.toFixed(1)}%
//                                 </div>
//                                 {progress.percentage > 0 && (
//                                   <span className={`ml-2 text-xs px-2 py-1 rounded-full ${progress.direction === 'up' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
//                                     <i className={`fas fa-arrow-${progress.direction} mr-1`}></i>
//                                     {progress.percentage.toFixed(1)}%
//                                   </span>
//                                 )}
//                               </div>
//                             </td>
//                             <td className="px-6 py-4">
//                               <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${gradeBadge.color}`}>
//                                 {subject.latest_score ? gradeBadge.text : 'N/A'}
//                               </span>
//                             </td>
//                             <td className="px-6 py-4">
//                               <div className="text-sm text-gray-900">
//                                 {subject.class_average?.toFixed(1) || '--'}%
//                               </div>
//                             </td>
//                             <td className="px-6 py-4">
//                               <div className="text-sm text-gray-900">
//                                 {subject.position ? `${subject.position}/${subject.total_students}` : '--'}
//                               </div>
//                             </td>
//                             <td className="px-6 py-4">
//                               <button
//                                 onClick={() => {
//                                   setSelectedSubject(subject);
//                                   setShowDetailsModal(true);
//                                 }}
//                                 className="text-sm text-indigo-600 hover:text-indigo-700 font-medium px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors"
//                               >
//                                 <i className="fas fa-chart-line mr-2"></i>
//                                 View Details
//                               </button>
//                             </td>
//                           </tr>
//                         );
//                       })}
//                     </tbody>
//                   </table>
//                 </div>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Subject Details Modal */}
//       <Modal
//         isOpen={showDetailsModal}
//         onClose={() => setShowDetailsModal(false)}
//         title={selectedSubject?.subject_name}
//         size="xl"
//       >
//         {selectedSubject && (
//           <div className="space-y-6">
//             {/* Subject Header */}
//             <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-2xl border border-indigo-100">
//               <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
//                 <div>
//                   <div className="text-sm text-gray-600 font-medium">Current Score</div>
//                   <div className={`text-3xl font-bold mt-2 ${getGradeColor(selectedSubject.latest_score).split(' ')[0]}`}>
//                     {selectedSubject.latest_score?.toFixed(1)}%
//                   </div>
//                 </div>
//                 <div>
//                   <div className="text-sm text-gray-600 font-medium">Grade</div>
//                   <div className="mt-2">
//                     <span className={`px-4 py-2 inline-flex text-sm font-bold rounded-full ${getGradeBadge(selectedSubject.latest_score).color}`}>
//                       {getGradeLetter(selectedSubject.latest_score)}
//                     </span>
//                   </div>
//                 </div>
//                 <div>
//                   <div className="text-sm text-gray-600 font-medium">Class Average</div>
//                   <div className="text-2xl font-bold text-gray-900 mt-2">
//                     {selectedSubject.class_average?.toFixed(1)}%
//                   </div>
//                 </div>
//                 <div>
//                   <div className="text-sm text-gray-600 font-medium">Position</div>
//                   <div className="text-2xl font-bold text-gray-900 mt-2">
//                     {selectedSubject.position ? `${selectedSubject.position}/${selectedSubject.total_students}` : '--'}
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Performance History */}
//             {selectedSubject.performance_history && selectedSubject.performance_history.length > 0 && (
//               <div>
//                 <h4 className="text-lg font-bold text-gray-900 mb-4">Performance History</h4>
//                 <div className="space-y-3">
//                   {selectedSubject.performance_history.map((record, index) => (
//                     <div key={index} className="p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
//                       <div className="flex justify-between items-center">
//                         <div>
//                           <div className="font-medium text-gray-900">
//                             {record.academic_session} • {record.term}
//                           </div>
//                           <div className="text-sm text-gray-500 mt-1">
//                             Class Average: {record.class_average?.toFixed(1)}%
//                           </div>
//                         </div>
//                         <div className="text-right">
//                           <div className={`text-xl font-bold ${getGradeColor(record.score).split(' ')[0]}`}>
//                             {record.score?.toFixed(1)}%
//                           </div>
//                           <div className="text-sm text-gray-500">
//                             Grade: {getGradeLetter(record.score)}
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}

//             {/* Performance Analysis */}
//             <div className="bg-gray-50 p-6 rounded-2xl">
//               <h4 className="text-lg font-bold text-gray-900 mb-4">Performance Analysis</h4>
//               <div className="space-y-4">
//                 {selectedSubject.latest_score >= 90 ? (
//                   <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
//                     <div className="flex items-start space-x-3">
//                       <div className="p-2 bg-emerald-100 rounded-lg">
//                         <i className="fas fa-star text-emerald-600"></i>
//                       </div>
//                       <div>
//                         <h5 className="font-bold text-emerald-800">Excellent Performance!</h5>
//                         <p className="text-emerald-700 mt-1">
//                           You're demonstrating exceptional understanding of {selectedSubject.subject_name}. 
//                           Consider exploring advanced topics or helping peers.
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 ) : selectedSubject.latest_score >= 80 ? (
//                   <div className="p-4 bg-green-50 border border-green-100 rounded-xl">
//                     <div className="flex items-start space-x-3">
//                       <div className="p-2 bg-green-100 rounded-lg">
//                         <i className="fas fa-thumbs-up text-green-600"></i>
//                       </div>
//                       <div>
//                         <h5 className="font-bold text-green-800">Very Good Performance</h5>
//                         <p className="text-green-700 mt-1">
//                           You have a solid understanding of {selectedSubject.subject_name}. 
//                           Focus on mastering complex concepts to reach the next level.
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 ) : selectedSubject.latest_score >= 70 ? (
//                   <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
//                     <div className="flex items-start space-x-3">
//                       <div className="p-2 bg-blue-100 rounded-lg">
//                         <i className="fas fa-chart-line text-blue-600"></i>
//                       </div>
//                       <div>
//                         <h5 className="font-bold text-blue-800">Good Performance</h5>
//                         <p className="text-blue-700 mt-1">
//                           You're making good progress in {selectedSubject.subject_name}. 
//                           Regular practice and review will help improve your scores.
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 ) : selectedSubject.latest_score >= 60 ? (
//                   <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
//                     <div className="flex items-start space-x-3">
//                       <div className="p-2 bg-amber-100 rounded-lg">
//                         <i className="fas fa-lightbulb text-amber-600"></i>
//                       </div>
//                       <div>
//                         <h5 className="font-bold text-amber-800">Satisfactory Performance</h5>
//                         <p className="text-amber-700 mt-1">
//                           You understand the basics of {selectedSubject.subject_name}. 
//                           Consider additional study sessions and asking questions in class.
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="p-4 bg-red-50 border border-red-100 rounded-xl">
//                     <div className="flex items-start space-x-3">
//                       <div className="p-2 bg-red-100 rounded-lg">
//                         <i className="fas fa-exclamation-circle text-red-600"></i>
//                       </div>
//                       <div>
//                         <h5 className="font-bold text-red-800">Needs Improvement</h5>
//                         <p className="text-red-700 mt-1">
//                           You may need extra help with {selectedSubject.subject_name}. 
//                           Consider meeting with your teacher to discuss learning strategies.
//                         </p>
//                       </div>
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>

//             <div className="flex justify-end pt-4 border-t border-gray-200">
//               <button
//                 onClick={() => setShowDetailsModal(false)}
//                 className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium transition-colors"
//               >
//                 Close Details
//               </button>
//             </div>
//           </div>
//         )}
//       </Modal>
//     </div>
//   );
// };

// export default MyGrades;